import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Grid,
  Avatar,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem
} from '@mui/material';
import {
  EmojiEvents,
  Group,
  Person,
  MoreVert,
  Add,
  PlayArrow,
  Schedule,
  People
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO, format } from 'date-fns';

const ChallengesList = ({ challenges, onJoin, onViewDetails, onLeave, loading, userChallenges = false, currentUser }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  const handleMenuOpen = (event, challenge) => {
    setAnchorEl(event.currentTarget);
    setSelectedChallenge(challenge);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedChallenge(null);
  };

  const getChallengeTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'team':
        return <Group />;
      case 'individual':
        return <Person />;
      default:
        return <EmojiEvents />;
    }
  };

  const getChallengeStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'upcoming':
      case 'scheduled':
        return 'warning';
      case 'completed':
        return 'default';
      case 'ending_soon':
        return 'error';
      default:
        return 'primary';
    }
  };

  const formatDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Duration not set';
    
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return '1 day';
      if (diffDays < 7) return `${diffDays} days`;
      if (diffDays === 7) return '1 week';
      if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks`;
      return `${Math.ceil(diffDays / 30)} months`;
    } catch (error) {
      return 'Duration not set';
    }
  };

  const formatChallengeDate = (dateString) => {
    if (!dateString) return 'Date not set';
    
    try {
      const date = parseISO(dateString);
      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      return 'Date not set';
    }
  };

  const getTimeRemaining = (endDate) => {
    if (!endDate) return null;
    
    try {
      const end = parseISO(endDate);
      const now = new Date();
      
      if (end < now) return 'Ended';
      
      return formatDistanceToNow(end, { addSuffix: true });
    } catch (error) {
      return null;
    }
  };

  if (!challenges || challenges.length === 0) {
    return (
      <Box textAlign="center" py={6}>
        <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {userChallenges ? 'No challenges joined yet' : 'No challenges available'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {userChallenges 
            ? 'Join a challenge to start competing with friends!' 
            : 'Check back later for new challenges!'
          }
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {challenges.map((challenge) => {
          const timeRemaining = getTimeRemaining(challenge.end_date);
          const isUserJoined = challenge.userJoined || challenge.user_joined;
          const progress = challenge.progress || challenge.user_progress || 0;
          const isOwner = currentUser && (challenge.creator_id === currentUser || challenge.created_by === currentUser);
          
          return (
            <Grid item xs={12} md={6} lg={4} key={challenge.challenge_id || challenge.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  position: 'relative',
                  '&:hover': {
                    boxShadow: 3
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  {/* Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" flex={1}>
                      <Avatar 
                        sx={{ 
                          mr: 1.5, 
                          bgcolor: challenge.challenge_type === 'team' ? 'primary.main' : 'secondary.main',
                          width: 40,
                          height: 40
                        }}
                      >
                        {getChallengeTypeIcon(challenge.challenge_type)}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="h6" component="div" noWrap>
                          {challenge.title || challenge.name || 'Untitled Challenge'}
                        </Typography>
                        <Box display="flex" gap={1} mt={0.5}>
                          <Chip 
                            label={challenge.status || 'Active'} 
                            color={getChallengeStatusColor(challenge.status)}
                            size="small"
                          />
                          <Chip 
                            label={challenge.challenge_type || 'Individual'} 
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Box>
                    
                    <IconButton 
                      size="small"
                      onClick={(e) => handleMenuOpen(e, challenge)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  {/* Description */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    paragraph
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {challenge.description || 'No description available'}
                  </Typography>

                  {/* Progress (if user joined) */}
                  {isUserJoined && (
                    <Box mb={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight="medium">
                          Your Progress
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {Math.round(progress)}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(progress, 100)} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  )}

                  {/* Challenge Details */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Schedule fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {formatDuration(challenge.start_date, challenge.end_date)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <People fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {challenge.participant_count || challenge.participantCount || 0} participants
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Dates */}
                  <Box mb={1}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Starts: {formatChallengeDate(challenge.start_date)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Ends: {formatChallengeDate(challenge.end_date)}
                    </Typography>
                    {timeRemaining && timeRemaining !== 'Ended' && (
                      <Typography variant="caption" color="primary.main" fontWeight="medium">
                        {timeRemaining}
                      </Typography>
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  {isOwner ? (
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      onClick={() => onViewDetails(challenge.challenge_id || challenge.id)}
                      startIcon={<PlayArrow />}
                    >
                      Manage Challenge
                    </Button>
                  ) : isUserJoined ? (
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      onClick={() => onViewDetails(challenge.challenge_id || challenge.id)}
                      startIcon={<PlayArrow />}
                    >
                      View Details
                    </Button>
                  ) : (
                    <Button 
                      fullWidth 
                      variant="contained" 
                      startIcon={<Add />}
                      onClick={() => onJoin(challenge.challenge_id || challenge.id)}
                      disabled={challenge.status === 'completed'}
                    >
                      Join Challenge
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          onViewDetails(selectedChallenge?.challenge_id || selectedChallenge?.id);
          handleMenuClose();
        }}>
          View Details
        </MenuItem>
        {selectedChallenge?.userJoined && onLeave && (
          <MenuItem 
            onClick={() => {
              onLeave(selectedChallenge?.challenge_id || selectedChallenge?.id);
              handleMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            Leave Challenge
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default ChallengesList;